/**
 * Firm config + dual-firm routing — money-module FOUNDATION slice.
 *
 * ─────────────────────────────────────────────────────────────────────────
 *  SOURCE OF TRUTH (do NOT re-ask the owner — all captured 2026-06-13):
 *    04_Projects/AVBP/AVBP_FIRM_BILLING_DETAILS.md
 *
 *  The firm OBJECT shape is lifted from clientflow-v3 (`DEF_FIRMS`, the
 *  richest existing model — legal name, GST flag/no, currencies, prefix,
 *  bank block, Razorpay/UPI, default tax, terms) and reconciled with the
 *  REAL billing file (GSTIN/PAN/Udyam, Kotak a/cs, SWIFT, SAC, LUT wording,
 *  confirmed Razorpay handles). This is the SINGLE firm source the money
 *  module reads — invoices, quotes, payments, reminders all consult it.
 *
 *  ROUTING RULE (LOCKED, owner — billing file header):
 *    USD             → Aayatan Veda (firm1)
 *    GST invoice     → Aayatan Veda (firm1)
 *    INR without GST → Rohit Khandelwal HUF (firm2)
 *  Auto-selected from {currency, gst}; admin-overridable per
 *  invoice/payment (the override is a later slice; `firmFor` is the pure
 *  auto-select that the override layers on top of).
 * ─────────────────────────────────────────────────────────────────────────
 *
 * PURE + dependency-light (no Firebase, no React) so the Node test runner
 * exercises the routing + config directly. The config is a vendored
 * constant for the foundation slice; a later slice may move it to a
 * `firms/{id}` Firestore doc (admin-editable) — `getFirm` / `FIRMS` are the
 * single seam that changes when that lands.
 */

export type FirmId = 'firm1' | 'firm2';
export type Currency = 'INR' | 'USD';

/** A bank block on a firm (for invoice / pay-instruction rendering). */
export interface FirmBank {
  bankName: string;
  accountName: string;
  accountNo: string;
  ifsc: string;
  upi: string;
  /** SWIFT/BIC — only on a firm that receives foreign (USD) wires. */
  swift?: string;
  /** AD/BSR code for foreign inward remittance (USD firm only). */
  adCode?: string;
}

/** A firm's full billing identity — the invoice/quote/payment header. */
export interface Firm {
  id: FirmId;
  /** Legal name as printed on the document. */
  legalName: string;
  /** Short display chip label (UI). */
  shortName: string;
  entityType: string;
  /** GST-registered? Drives Tax-Invoice vs Invoice + GST math gating. */
  gstRegistered: boolean;
  gstin: string | null;
  pan: string;
  udyam: string;
  /** State + numeric code — drives intra-state CGST+SGST vs inter-state IGST. */
  state: string;
  stateCode: string;
  registeredAddress: string;
  /** Allowed billing currencies (HUF is INR-only; AV does INR + USD). */
  currencies: Currency[];
  /** Invoice/quote number prefix (firm-scoped numbering). */
  prefix: string;
  /** Default GST rate % when this firm bills GST (0 for the exempt firm). */
  defaultGstRate: number;
  /** SAC code for the service line ('' until CA-confirmed). */
  sac: string;
  bank: FirmBank;
  /** Confirmed Razorpay pay-link handle. */
  razorpay: string;
  /** Export / LUT wording for USD invoices (only the export-capable firm). */
  exportWording?: string;
  /** Shared contact line printed on the document. */
  contactPhone: string;
  contactEmail: string;
  /** Optional website line on the document. */
  website?: string;
}

/**
 * The two firms, seeded VERBATIM from AVBP_FIRM_BILLING_DETAILS.md
 * (2026-06-13 — banking + identity + GST COMPLETE; both on Kotak).
 */
export const FIRMS: Record<FirmId, Firm> = {
  firm1: {
    id: 'firm1',
    legalName: 'Aayatan Veda',
    shortName: 'Aayatan Veda',
    entityType: 'Proprietorship (Shri Rohit Khandelwal)',
    gstRegistered: true,
    gstin: '22ARRPK8036C2ZU',
    pan: 'ARRPK8036C',
    udyam: 'UDYAM-CG-14-0014671',
    state: 'Chhattisgarh',
    stateCode: '22',
    registeredAddress:
      'E78, Sector 1, Devendra Nagar, Raipur, Chhattisgarh — 492001',
    currencies: ['INR', 'USD'],
    prefix: 'AV1-',
    defaultGstRate: 18,
    sac: '998393',
    bank: {
      bankName: 'Kotak Mahindra Bank',
      accountName: 'Aayatan Veda',
      accountNo: '1402198230',
      ifsc: 'KKBK0006402',
      upi: 'aayatanveda@kotak',
      swift: 'KKBKINBBXXX',
      adCode: '01813987600009',
    },
    razorpay: 'razorpay.me/@aayatanveda',
    exportWording:
      'Supply meant for Export under Letter of Undertaking (LUT) without ' +
      'payment of Integrated Tax in terms of Rule 96A of the CGST Rules, 2017',
    contactPhone: '9406292000',
    contactEmail: 'onlyramram@gmail.com',
    website: 'aayatanveda.com',
  },
  firm2: {
    id: 'firm2',
    legalName: 'Rohit Khandelwal (HUF)',
    shortName: 'RKH HUF',
    entityType: 'Hindu Undivided Family',
    gstRegistered: false,
    gstin: null,
    pan: 'AANHR6982D',
    udyam: 'UDYAM-CG-14-0109028',
    state: 'Chhattisgarh',
    stateCode: '22',
    registeredAddress:
      'E78, Sector 01, Pandari, Devendra Nagar, Raipur, Chhattisgarh — 492004',
    currencies: ['INR'],
    prefix: 'RKH-',
    defaultGstRate: 0,
    sac: '998393',
    bank: {
      bankName: 'Kotak Mahindra Bank',
      accountName: 'Rohit Khandelwal (HUF)',
      accountNo: '1402198250',
      ifsc: 'KKBK0006402',
      upi: 'onlyramram@kotak',
    },
    razorpay: 'razorpay.me/@vastuarchitectrohitkhandelwal',
    contactPhone: '9406292000',
    contactEmail: 'onlyramram@gmail.com',
  },
};

/** The two firms as an array (UI iteration / per-firm splits). */
export const ALL_FIRMS: Firm[] = [FIRMS.firm1, FIRMS.firm2];

/**
 * PURE — the static-payment slots for a firm, derived from the locked FIRMS
 * bank details. Field names match the Firestore `firms/{id}` overlay + the CF
 * `loadFirmPayment` (upiId / bankAccName / bankAccNo / bankIfsc / bankName).
 * `qrUrl` is only ever a Firestore field (a static constant cannot carry an
 * uploaded QR), so it is null here — the impure overlay fills it. This keeps
 * the SEND preview non-empty even before Varun populates the Firestore docs.
 */
export function firmPaymentFromStatic(id: FirmId | string | null | undefined): {
  upiId: string | null;
  bankAccName: string | null;
  bankAccNo: string | null;
  bankIfsc: string | null;
  bankName: string | null;
  qrUrl: string | null;
  swift: string | null;
  bsrAdCode: string | null;
} {
  const f = getFirm(id);
  return {
    upiId: f.bank.upi || null,
    bankAccName: f.bank.accountName || null,
    bankAccNo: f.bank.accountNo || null,
    bankIfsc: f.bank.ifsc || null,
    bankName: f.bank.bankName || null,
    qrUrl: null,
    // SWIFT/AD-code only exist on the USD-capable firm (firm1).
    swift: f.bank.swift || null,
    bsrAdCode: f.bank.adCode || null,
  };
}

/** Lookup a firm by id (defensive — unknown id → firm1, the GST default). */
export function getFirm(id: FirmId | string | null | undefined): Firm {
  if (id === 'firm2') return FIRMS.firm2;
  return FIRMS.firm1;
}

export interface FirmRoutingInput {
  currency: Currency;
  /** Is this a GST (tax) invoice? */
  gst: boolean;
}

/**
 * THE LOCKED ROUTING RULE (pure, owner-dictated):
 *   USD              → firm1 (Aayatan Veda) — the only USD-capable firm.
 *   GST invoice      → firm1 (Aayatan Veda) — the only GST-registered firm.
 *   INR without GST  → firm2 (Rohit Khandelwal HUF).
 *
 * Returns the AUTO-SELECTED firm id. An admin override (a later slice)
 * supplies an explicit firmId that supersedes this — but it can never pick
 * a firm the currency forbids (USD on HUF), so the override layer must
 * re-validate against `firm.currencies` / `firm.gstRegistered`.
 */
export function firmFor(input: FirmRoutingInput): FirmId {
  if (input.currency === 'USD') return 'firm1';
  if (input.gst) return 'firm1';
  return 'firm2';
}

/**
 * Validate an (explicit/override) firm choice against the currency + GST
 * intent. Returns a human reason when the choice is INVALID (e.g. USD on
 * HUF, or a GST invoice on the exempt firm), else null. The override UI
 * uses this to block an impossible manual selection.
 */
export function firmChoiceError(
  firmId: FirmId,
  input: FirmRoutingInput,
): string | null {
  const firm = getFirm(firmId);
  if (input.currency === 'USD' && !firm.currencies.includes('USD')) {
    return `${firm.shortName} cannot bill in USD — only Aayatan Veda receives foreign remittance.`;
  }
  if (input.gst && !firm.gstRegistered) {
    return `${firm.shortName} is GST-exempt — a GST tax invoice must be raised by Aayatan Veda.`;
  }
  return null;
}

/**
 * Firm-scoped document number: `<PREFIX><FY>-<NNNN>`, e.g. `AV1-25-26-0007`.
 * The numbering is per-firm AND per-financial-year (Indian FY = Apr→Mar).
 * `seq` is the per-firm-per-FY running serial (minted from a Firestore
 * counter in the impure layer — a later slice). PURE so the format is
 * unit-tested. `fyLabel` like `25-26`.
 */
export function firmDocNumber(
  firmId: FirmId,
  fyLabel: string,
  seq: number,
): string {
  return `${getFirm(firmId).prefix}${fyLabel}-${String(seq).padStart(4, '0')}`;
}

/**
 * Indian financial-year label (`YY-YY`) for an ISO date — the FY runs
 * 1 Apr → 31 Mar, so Jan–Mar belongs to the PREVIOUS calendar year's FY.
 * e.g. 2026-06-13 → '26-27'; 2026-02-01 → '25-26'. Pure.
 */
export function fyLabelFor(iso: string): string {
  const d = new Date(iso);
  const y = d.getUTCFullYear();
  const m = d.getUTCMonth(); // 0=Jan
  const startYear = m >= 3 ? y : y - 1; // Apr (3) onward = this FY
  const a = String(startYear % 100).padStart(2, '0');
  const b = String((startYear + 1) % 100).padStart(2, '0');
  return `${a}-${b}`;
}
